import { useCallback, useState } from 'react';

/** Vrijednost polja u nacrtu — '' dok je polje prazno (to je greška, ne 0). */
export type DraftValue = number | '';
export type DraftValues = Record<string, DraftValue>;

export interface AddedDraftItem<TMeta> {
  tempId: string;
  meta: TMeta;
  values: DraftValues;
}

export interface DraftSummary {
  changed: number;
  added: number;
  removed: number;
  errors: number;
  total: number;
}

let tempCounter = 0;

/**
 * Nespremljene promjene stavki jednog entiteta (eksplicitno "Spremi stavke" umjesto automatskog
 * spremanja po polju). Spremljene stavke dolaze sa servera i ostaju netaknute; nacrt drži samo
 * razlike: izmjene po stavci, stavke označene za uklanjanje i nove stavke. Osvježavanje stavki sa
 * servera zato ne briše nacrt.
 */
export const useItemsDraft = <TMeta>() => {
  const [edits, setEdits] = useState<Record<string, DraftValues>>({});
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [added, setAdded] = useState<AddedDraftItem<TMeta>[]>([]);

  /** Vrijednost polja: nacrt ako postoji, inače spremljena. */
  const valueOf = useCallback(
    (id: string, field: string, savedValue: number): DraftValue => {
      const row = edits[id];
      return row && field in row ? row[field] : savedValue;
    },
    [edits]
  );

  const setValue = useCallback((id: string, field: string, value: DraftValue) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }, []);

  const isRemoved = useCallback((id: string) => removedIds.includes(id), [removedIds]);

  const toggleRemoved = useCallback((id: string) => {
    setRemovedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const addItems = useCallback((items: { meta: TMeta; values: DraftValues }[]) => {
    setAdded((prev) => [
      ...prev,
      ...items.map((item) => ({ ...item, tempId: `new-${++tempCounter}` }))
    ]);
  }, []);

  const setAddedValue = useCallback((tempId: string, field: string, value: DraftValue) => {
    setAdded((prev) =>
      prev.map((item) =>
        item.tempId === tempId ? { ...item, values: { ...item.values, [field]: value } } : item
      )
    );
  }, []);

  /** Nova stavka se ne označava za uklanjanje — samo nestane iz nacrta. */
  const discardAdded = useCallback((tempId: string) => {
    setAdded((prev) => prev.filter((item) => item.tempId !== tempId));
  }, []);

  const reset = useCallback(() => {
    setEdits({});
    setRemovedIds([]);
    setAdded([]);
  }, []);

  return {
    edits,
    removedIds,
    added,
    valueOf,
    setValue,
    isRemoved,
    toggleRemoved,
    addItems,
    setAddedValue,
    discardAdded,
    reset
  };
};

/** Polja retka koja se razlikuju od spremljenih vrijednosti. */
export const changedFields = (
  values: DraftValues,
  saved: Record<string, number | undefined>
): string[] => Object.keys(values).filter((field) => values[field] !== (saved[field] ?? 0));
